import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import OTPVerification from '../components/OTPVerification';
import api from '../services/api';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verifiedOTP, setVerifiedOTP] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await api.post('/otp/send', {
        email,
        purpose: 'password-reset'
      });

      if (response.data.success) {
        setSuccess('OTP sent to your email!');
        setStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerified = (otp) => {
    setVerifiedOTP(otp);
    setStep(3);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/reset-password', {
        email,
        otp: verifiedOTP,
        new_password: newPassword
      });

      setSuccess(response.data.message);
      setTimeout(() => {
        navigate('/signin');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Header />
      <main>
        <section className="section" style={{ maxWidth: '500px', margin: '40px auto' }}>
          {/* Step 1: Enter Email */}
          {step === 1 && (
            <div className="card" style={{ padding: '40px 32px' }}>
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>🔐</div>
                <h1 style={{
                  color: 'var(--maroon)',
                  fontFamily: "'Playfair Display',serif",
                  fontSize: '32px',
                  marginBottom: '8px'
                }}>
                  Forgot Password?
                </h1>
                <p style={{ color: 'var(--muted)', fontSize: '15px' }}>
                  Enter your email address and we'll send you a code to reset your password
                </p>
              </div>

              {error && (
                <div style={{
                  background: 'rgba(220, 53, 69, 0.1)',
                  border: '1px solid rgba(220, 53, 69, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '20px',
                  color: '#dc3545',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSendOTP}>
                <label>Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  required
                  disabled={loading}
                />

                <button
                  type="submit"
                  disabled={loading}
                  style={{ width: '100%', marginTop: '16px' }}
                >
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </button>
              </form>

              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <Link to="/signin" style={{ color: 'var(--maroon)', fontSize: '14px' }}>
                  ← Back to Sign In
                </Link>
              </div>
            </div>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <OTPVerification
              email={email}
              purpose="password-reset"
              onVerified={handleOTPVerified}
              onCancel={() => setStep(1)}
              title="Verify Your Email"
              subtitle="Enter the 6-digit code sent to"
              skipVerification={true}
            />
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <div className="card" style={{ padding: '40px 32px' }}>
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>🔑</div>
                <h1 style={{
                  color: 'var(--maroon)',
                  fontFamily: "'Playfair Display',serif",
                  fontSize: '32px',
                  marginBottom: '8px'
                }}>
                  Set New Password
                </h1>
                <p style={{ color: 'var(--muted)', fontSize: '15px' }}>
                  Create a strong password for your account
                </p>
              </div>

              {error && (
                <div style={{
                  background: 'rgba(220, 53, 69, 0.1)',
                  border: '1px solid rgba(220, 53, 69, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '20px',
                  color: '#dc3545',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}

              {success && (
                <div style={{
                  background: 'rgba(40, 167, 69, 0.1)',
                  border: '1px solid rgba(40, 167, 69, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '20px',
                  color: '#28a745',
                  fontSize: '14px'
                }}>
                  {success}
                </div>
              )}

              <form onSubmit={handleResetPassword}>
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  disabled={loading}
                  minLength={6}
                />

                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  disabled={loading}
                  minLength={6}
                />

                <button
                  type="submit"
                  disabled={loading}
                  style={{ width: '100%', marginTop: '16px' }}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ForgotPassword;
